import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GeneralContext, useDataContext } from '#/hooks/useDataContext';
import { useResponsiveFormFactor } from '#/hooks/useResponsiveValue';
import { FormFactors } from '#/service/constants';
import {
  snackbarActions,
  SnackbarOptions,
  useSnackbarStore,
} from '#/store/snackbar/snackbar.store';
import { tb } from '#/store/translation/translation.store';

const DEFAULT_DURATION = 3000;
const ON_MOUSE_LEAVE_DURATION = 500;

const evaluateConditions = (
  snackbar: SnackbarOptions,
  formFactor: FormFactors,
  bindingContext: GeneralContext
) => {
  const hasMessage = !!snackbar?.message;
  const hasAction = !!(snackbar?.actionText && snackbar?.onAction);

  return {
    hasAction,
    isTextCentered: !hasAction && formFactor !== FormFactors.MOBILE,
    message: hasMessage ? tb(snackbar.message, bindingContext) : undefined,
    actionText: hasAction ? tb(snackbar.actionText, bindingContext) : undefined,
  };
};

export const useSnackbar = () => {
  const { isVisible, queue } = useSnackbarStore();
  const { getGeneralContext } = useDataContext();
  const snackbar = queue[0];
  const formFactor = useResponsiveFormFactor();
  const [show, setShow] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const bindingContext = useMemo(
    () => getGeneralContext(),
    [getGeneralContext]
  );

  const startTimer = useCallback(
    (duration?: number) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(
        () => {
          setShow(false); // trigger exit animation
        },
        duration ?? snackbar?.duration ?? DEFAULT_DURATION
      );
    },
    [snackbar?.duration]
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handleMouseLeave = useCallback(() => {
    if (isVisible && snackbar) {
      startTimer(ON_MOUSE_LEAVE_DURATION);
    }
  }, [isVisible, snackbar, startTimer]);

  const handleAction = useCallback(() => {
    if (snackbar?.onAction) {
      setShow(false); // trigger exit animation
      snackbar.onAction();
    }
  }, [snackbar]);

  const handleExited = useCallback(() => {
    snackbarActions.removeFromQueue();
  }, []);

  // Show/hide logic with animation and timer
  useEffect(() => {
    if (isVisible && snackbar) {
      if (!snackbar.message) {
        // Invalid snackbar — remove immediately to unblock the queue
        snackbarActions.removeFromQueue();

        return;
      }

      setShow(true);
      startTimer();
    } else {
      setShow(false);
      clearTimer();
    }

    return () => {
      clearTimer();
    };
  }, [isVisible, snackbar, startTimer, clearTimer]);

  // Computed values
  const { message, actionText, hasAction, isTextCentered } = evaluateConditions(
    snackbar,
    formFactor,
    bindingContext
  );

  return {
    show,
    formFactor,
    hasAction,
    isTextCentered,
    message,
    actionText,
    handleMouseEnter,
    handleMouseLeave,
    handleAction,
    handleExited,
  };
};
